import {
  json,
  type LoaderFunctionArgs,
  type MetaFunction,
} from "@remix-run/node";
import { useLoaderData } from "@remix-run/react";
import Footer from "#app/components/footer";
import { LandingBackground } from "#app/components/landing/landing-background";
import { LandingHeader } from "#app/components/landing/landing-header";
import {
  PricingSection,
  type Plan,
} from "#app/components/landing/pricing-section";
import { getKeycloakId, getUserId } from "#app/utils/auth.server.ts";
import { prisma } from "#app/utils/db.server.ts";
import { createKeycloakAdminService } from "#app/utils/keycloak-admin.server.ts";
import { canManageOrgBilling } from "#app/utils/stripe-subscription-scope.server.ts";
import { stripe } from "#app/utils/stripe.server.ts";

export const meta: MetaFunction = () => [
  { title: "Pricing | Supervity AI" },
  {
    name: "description",
    content: "Choose a plan that fits your workflow automation needs.",
  },
];

export async function loader({ request }: LoaderFunctionArgs) {
  const [userId, dbPlans] = await Promise.all([
    getUserId(request),
    prisma.plan.findMany({
      where: { active: true },
      select: {
        id: true,
        planName: true,
        description: true,
        billingCycle: true,
        features: true,
        stripeProductId: true,
      },
      orderBy: { id: "asc" },
    }),
  ]);

  let organizations: Array<{ id: string; name: string }> = [];
  let userActivePlans: Record<
    string,
    { planId: string; billingCycle: string }
  > = {};
  if (userId) {
    try {
      const keycloakId = await getKeycloakId(request);
      if (keycloakId) {
        const keycloakAdmin = createKeycloakAdminService();
        const userInfo = await keycloakAdmin.getUserInfo(keycloakId);
        if (userInfo) {
          organizations = userInfo.organizations
            .filter((o) => canManageOrgBilling(o.roles))
            .map((o) => ({
              id: o.id,
              name: o.name,
            }));
        }
      }
    } catch (error) {
      console.error(
        "pricing loader: failed to load Keycloak organizations",
        error,
      );
    }

    try {
      const ugps = await prisma.userGroupPlan.findMany({
        where: { ownerId: userId, active: true },
        select: {
          organizationId: true,
          ownerId: true,
          planId: true,
          billingCycle: true,
        },
      });
      for (const ugp of ugps) {
        const entry = {
          planId: ugp.planId,
          billingCycle: ugp.billingCycle,
        };
        if (ugp.organizationId === ugp.ownerId) {
          userActivePlans["personal"] = entry;
        } else {
          userActivePlans[ugp.organizationId] = entry;
        }
      }
    } catch (error) {
      console.error("pricing loader: failed to load user plans", error);
    }
  }

  const plans = await Promise.all(
    dbPlans.map(async (plan) => {
      let stripeMonthlyPrice: number | null = null;
      let stripeAnnualPrice: number | null = null;

      if (plan.stripeProductId && stripe) {
        try {
          const prices = await stripe.prices.list({
            product: plan.stripeProductId,
            active: true,
          });
          const monthly = prices.data.find(
            (p) => p.recurring?.interval === "month",
          );
          const annual = prices.data.find(
            (p) => p.recurring?.interval === "year",
          );

          if (monthly?.unit_amount)
            stripeMonthlyPrice = monthly.unit_amount / 100;
          if (annual?.unit_amount) stripeAnnualPrice = annual.unit_amount / 100;
        } catch (e) {
          console.error("Failed to fetch stripe prices for plan", plan.id, e);
        }
      }

      return {
        id: plan.id,
        planName: plan.planName,
        description: plan.description,
        billingCycle: plan.billingCycle,
        features: plan.features as Record<string, unknown>,
        stripeMonthlyPrice,
        stripeAnnualPrice,
      };
    }),
  );

  return json({
    plans,
    isLoggedIn: Boolean(userId),
    organizations,
    userActivePlans,
  });
}

export const handle = { i18n: ["marketing", "footer", "common"] };

export default function Pricing() {
  const { plans, isLoggedIn, organizations, userActivePlans } =
    useLoaderData<typeof loader>();

  return (
    <main className="relative min-h-dvh bg-background font-poppins">
      <LandingBackground />
      <LandingHeader showLogin landingPrompt="" />
      <div className="min-h-[calc(100dvh-72px)] flex flex-col pt-24">
        <PricingSection
          id="pricing"
          plans={plans as Plan[]}
          isLoggedIn={isLoggedIn}
          organizations={organizations}
          userActivePlans={userActivePlans}
        />
      </div>
      <Footer />
    </main>
  );
}
